import { NextPage } from 'next';
import Link from 'next/link';
import { gql } from '@apollo/client';
import styled from 'styled-components';
import { Header, HEADER_QUERY } from '../../components/header';
import { Footer, FOOTER_QUERY } from '../../components/footer';
import { ContentStackClient } from '../../lib/contentstack';
import { ResponsiveContainer } from '../../components/responsive';
import { SensorFusionPipeline } from '../../components/sensor-fusion-pipeline';
import { mediaScreen } from '../../utils/mediaScreen';
import { Footer2025, Header2025 } from '../../types/contentstack';
import { sensorFusionContent as content } from '../../content/sensor-fusion';

/* ------------------------------------------------------------------ *
 *  Layout primitives
 * ------------------------------------------------------------------ */

// Every band uses the site's ResponsiveContainer, the same one the header
// uses, so the headline, the pipeline and the sections below all start on the
// logo's left edge at every breakpoint.
const HeroWrap = styled(ResponsiveContainer)`
  position: relative;
  z-index: 1;
  box-sizing: border-box;
`;

const WideWrap = styled(ResponsiveContainer)`
  box-sizing: border-box;
`;

const ReadWrap = styled(ResponsiveContainer)`
  box-sizing: border-box;
`;

/* ------------------------------------------------------------------ *
 *  Hero
 * ------------------------------------------------------------------ */

const Hero = styled.section`
  position: relative;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.slate1};

  .hero-dots {
    position: absolute;
    inset: 0;
    background-image: url('/img/dots-bg.svg');
    background-size: cover;
    background-position: center;
    opacity: 0.35;
  }

  .hero-glow {
    position: absolute;
    inset: 0;
    background: radial-gradient(
      1000px 560px at 76% 6%,
      rgba(81, 192, 172, 0.16),
      transparent 62%
    );
  }

  .hero-inner {
    padding: 168px 0 64px;

    ${mediaScreen('xs', 'md')} {
      padding: 124px 0 40px;
    }
  }
`;

const HeroEyebrow = styled.div`
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 4px;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.teal};
  margin-bottom: 30px;

  ${mediaScreen('xs', 'md')} {
    margin-bottom: 20px;
  }
`;

const HeroHeadline = styled.h1`
  font-size: clamp(38px, 5.6vw, 76px);
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: -1.4px;
  color: ${({ theme }) => theme.colors.white};
  margin: 0;
  max-width: 980px;
  text-wrap: balance;

  span {
    color: ${({ theme }) => theme.colors.teal};
    display: block;
  }

  ${mediaScreen('xs', 'md')} {
    letter-spacing: -0.8px;
  }
`;

const HeroIntro = styled.div`
  max-width: 720px;
  margin-top: 40px;
  font-size: 21px;
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.78);
  font-weight: 400;

  p {
    margin: 0 0 20px;
  }

  p:last-child {
    margin: 0;
    color: ${({ theme }) => theme.colors.white};
  }

  ${mediaScreen('xs', 'md')} {
    margin-top: 28px;
    font-size: 18px;
  }
`;

/* ------------------------------------------------------------------ *
 *  Pipeline
 * ------------------------------------------------------------------ */

const Pipeline = styled.section`
  background: ${({ theme }) => theme.colors.slate1};
  padding: 0 0 88px;

  ${mediaScreen('xs', 'md')} {
    padding: 0 0 56px;
  }
`;

// The frame keeps the drawing's own proportions on wide screens. On a phone
// it runs edge to edge and is tall enough to read one stage at a time.
const PipelineFrame = styled.div`
  position: relative;
  aspect-ratio: 1920 / 800;
  border: 1px solid rgba(128, 146, 178, 0.22);
  border-radius: 14px;
  overflow: hidden;
  background: ${({ theme }) => theme.colors.slate1};

  ${mediaScreen('xs', 'sm')} {
    aspect-ratio: auto;
    height: clamp(460px, 138vw, 560px);
    margin: 0 -32px;
    border-left: 0;
    border-right: 0;
    border-radius: 0;
  }
`;

const PipelineHint = styled.p`
  display: none;
  margin: 12px 0 0;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.slate5};

  ${mediaScreen('xs', 'sm')} {
    display: block;
  }
`;

// The three stages, in the order the data moves. The numbers are that order.
const Stages = styled.ol`
  list-style: none;
  margin: 44px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 44px;

  ${mediaScreen('xs', 'md')} {
    grid-template-columns: 1fr;
    gap: 0;
    margin-top: 28px;
  }

  li {
    border-top: 1px solid rgba(128, 146, 178, 0.22);
    padding-top: 20px;

    ${mediaScreen('xs', 'md')} {
      padding: 22px 0;
    }
  }

  .stage-head {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 4px 12px;
    margin: 0 0 10px;
  }

  .step {
    font-family: 'SFMono-Regular', ui-monospace, 'Menlo', monospace;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.teal};
  }

  .name {
    font-size: 24px;
    font-weight: 700;
    letter-spacing: -0.3px;
    color: ${({ theme }) => theme.colors.white};
  }

  .where {
    font-size: 16px;
    color: ${({ theme }) => theme.colors.slate5};
  }

  p {
    margin: 0;
    font-size: 17px;
    line-height: 1.62;
    color: rgba(255, 255, 255, 0.76);
  }
`;

/* ------------------------------------------------------------------ *
 *  Sections
 * ------------------------------------------------------------------ */

const Section = styled.section<{ light?: boolean }>`
  padding: 92px 0;
  background: ${({ theme, light }) =>
    light ? theme.colors.white : theme.colors.slate1};

  ${mediaScreen('xs', 'md')} {
    padding: 56px 0;
  }
`;

const SectionHeadline = styled.h2<{ light?: boolean }>`
  font-size: clamp(30px, 3.6vw, 40px);
  font-weight: 700;
  line-height: 1.14;
  letter-spacing: -0.6px;
  color: ${({ theme, light }) =>
    light ? theme.colors.slate1 : theme.colors.white};
  margin: 0;
  text-wrap: balance;
`;

// Headline and lead on the left, the record on the right; stacked when narrow.
const Split = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 5fr) minmax(0, 7fr);
  gap: 72px;
  align-items: start;

  ${mediaScreen('xs', 'md')} {
    grid-template-columns: 1fr;
    gap: 32px;
  }

  .lead {
    margin: 24px 0 0;
    font-size: 20px;
    line-height: 1.6;
    color: rgba(25, 31, 44, 0.8);

    ${mediaScreen('xs', 'md')} {
      font-size: 18px;
      margin-top: 18px;
    }
  }
`;

// What a pose carries, set as one record with a row per field, the way the
// pose itself is laid out. One object, so one border, not four cards.
const PoseRecord = styled.dl`
  margin: 0;
  border: 1px solid rgba(25, 31, 44, 0.14);
  border-radius: 14px;
  overflow: hidden;

  .row {
    display: grid;
    grid-template-columns: 150px minmax(0, 1fr);
    gap: 20px;
    padding: 22px 26px;
    border-top: 1px solid rgba(25, 31, 44, 0.1);

    ${mediaScreen('xs', 'sm')} {
      grid-template-columns: 1fr;
      gap: 6px;
      padding: 18px 20px;
    }
  }

  .row:first-child {
    border-top: 0;
  }

  dt {
    font-size: 18px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.eb1};
  }

  dd {
    margin: 0;
    font-size: 17px;
    line-height: 1.55;
    color: rgba(25, 31, 44, 0.82);
  }
`;

const Prose = styled.div`
  max-width: 760px;
  margin-top: 30px;
  font-size: 20px;
  line-height: 1.68;
  color: rgba(255, 255, 255, 0.8);

  p {
    margin: 0 0 22px;
  }

  p:last-child {
    margin: 0;
  }

  ${mediaScreen('xs', 'md')} {
    font-size: 18px;
    margin-top: 22px;
  }
`;

const Sites = styled.ul`
  list-style: none;
  margin: 52px 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 40px;

  ${mediaScreen('xs', 'md')} {
    grid-template-columns: 1fr;
    gap: 0;
    margin-top: 32px;
  }

  li {
    border-top: 1px solid rgba(128, 146, 178, 0.22);
    padding-top: 18px;

    ${mediaScreen('xs', 'md')} {
      padding: 18px 0;
    }
  }

  .site-name {
    font-size: 19px;
    font-weight: 700;
    color: ${({ theme }) => theme.colors.white};
    margin: 0 0 6px;
  }

  p {
    margin: 0;
    font-size: 16px;
    line-height: 1.58;
    color: rgba(255, 255, 255, 0.72);
  }
`;

const Related = styled.p`
  margin: 56px 0 0;
  font-size: 18px;
  color: ${({ theme }) => theme.colors.slate5};

  a {
    color: ${({ theme }) => theme.colors.teal};
    font-weight: 500;
    text-decoration: underline;
    text-underline-offset: 4px;
  }

  a:hover {
    color: ${({ theme }) => theme.colors.lightTeal};
  }

  a:focus-visible {
    outline: 2px solid ${({ theme }) => theme.colors.teal};
    outline-offset: 3px;
    border-radius: 2px;
  }

  ${mediaScreen('xs', 'md')} {
    margin-top: 36px;
    font-size: 17px;
  }
`;

/* ------------------------------------------------------------------ *
 *  Page
 * ------------------------------------------------------------------ */

interface ISensorFusionPageProps {
  header?: Header2025;
  footer?: Footer2025;
}

// The Sensor Fusion page under /platform: the pipeline animation and the
// description that goes with it. Header and footer come from Contentstack;
// the page's own copy is in src/content/sensor-fusion.ts.
const SensorFusionPage: NextPage<ISensorFusionPageProps> = ({
  header,
  footer,
}) => {
  const { seo, hero, pipeline, pose, graph, related, cta } = content;

  return (
    <>
      <Header
        pageTitle={seo.title}
        pageDescription={seo.description}
        {...header}
      />

      <Hero>
        <div className="hero-dots" />
        <div className="hero-glow" />
        <HeroWrap>
          <div className="hero-inner">
            <HeroEyebrow>{hero.eyebrow}</HeroEyebrow>
            <HeroHeadline>
              {hero.headline} <span>{hero.headlineHighlight}</span>
            </HeroHeadline>
            <HeroIntro>
              {hero.intro.map((para) => (
                <p key={para}>{para}</p>
              ))}
            </HeroIntro>
          </div>
        </HeroWrap>
      </Hero>

      <Pipeline>
        <WideWrap>
          <PipelineFrame>
            <SensorFusionPipeline label={pipeline.diagramLabel} />
          </PipelineFrame>
          <PipelineHint>{pipeline.hint}</PipelineHint>

          <Stages>
            {pipeline.stages.map((stage) => (
              <li key={stage.name}>
                <div className="stage-head">
                  <span className="step">{stage.step}</span>
                  <span className="name">{stage.name}</span>
                  <span className="where">{stage.where}</span>
                </div>
                <p>{stage.text}</p>
              </li>
            ))}
          </Stages>
        </WideWrap>
      </Pipeline>

      <Section light>
        <ReadWrap>
          <Split>
            <div>
              <SectionHeadline light>{pose.headline}</SectionHeadline>
              <p className="lead">{pose.intro}</p>
            </div>
            <PoseRecord>
              {pose.fields.map((field) => (
                <div className="row" key={field.name}>
                  <dt>{field.name}</dt>
                  <dd>{field.text}</dd>
                </div>
              ))}
            </PoseRecord>
          </Split>
        </ReadWrap>
      </Section>

      <Section>
        <ReadWrap>
          <SectionHeadline>{graph.headline}</SectionHeadline>
          <Prose>
            {graph.paragraphs.map((para) => (
              <p key={para}>{para}</p>
            ))}
          </Prose>

          <Sites>
            {graph.sites.map((site) => (
              <li key={site.name}>
                <h3 className="site-name">{site.name}</h3>
                <p>{site.text}</p>
              </li>
            ))}
          </Sites>

          <Related>
            {related.lead}: <Link href={related.href}>{related.label}</Link>
          </Related>
        </ReadWrap>
      </Section>

      {/* Closing CTA: the standard Footer pre-footer band. */}
      <Footer
        marginTop={false}
        eyebrow={cta.eyebrow}
        headline={cta.headline}
        primary_button={cta.primary}
        secondary_button={cta.secondary}
        sections={footer?.sections}
      />
    </>
  );
};

// Fetches only the shared header and footer; the page's copy is local.
export async function getServerSideProps() {
  try {
    const { data } = await ContentStackClient().query<ISensorFusionPageProps>({
      query: PAGE_QUERY,
    });

    return { props: { ...data } };
  } catch (e) {
    return { props: {} };
  }
}

export default SensorFusionPage;

const PAGE_QUERY = gql`
  query sensorFusionQuery {
    ${HEADER_QUERY}
    ${FOOTER_QUERY}
  }
`;
